import { state, hasAnimation } from '../state.ts';
import { findCamera } from '../camera/types.ts';
import { PRESETS, getPreset } from '../camera/presets.ts';

export interface CameraPanelHandlers {
  /** Skeleton actors offered as preset subjects. */
  getSubjects: () => { id: string; label: string }[];
  onApplyPreset: (presetId: string, subjectActorId: string | null) => void;
  onSelectCamera: (id: string) => void;
  /** Toggle looking through a camera (switches back to 'free' when active). */
  onLookThrough: (id: string) => void;
  onDeleteCamera: (id: string) => void;
  onRenameCamera: (id: string, name: string) => void;
  onCameraColor: (id: string, hex: string) => void;
  onSetView: (view: 'free' | 'program') => void;
  /** A preset slider changed: update the param and re-bake the keyframes. */
  onPresetParam: (cameraId: string, key: string, value: number) => void;
  onShakeChange: (cameraId: string, patch: { enabled?: boolean; amount?: number; freq?: number }) => void;
  /** Reverse mapping for the shake Amount slider (0–100 display value). */
  shakeAmountOf: (posAmp: number) => number;
  /** Add/update a keyframe at the playhead from the current Free view. */
  onSetKeyFromView: () => void;
  onDeleteKey: () => void;
  onKeyEasing: (easing: string) => void;
  /** Change what the camera looks at (tracking target). */
  onAimChange: (
    cameraId: string,
    mode: 'path' | 'bone' | 'point' | 'group',
    opts?: { actorId?: string; bone?: string; heightOffset?: number }
  ) => void;
  /** Standalone *.cameras.json export / import. */
  onExportCameras: () => void;
  onImportCameras: (file: File) => void;
}

/**
 * Sidebar "Cameras" section: view toggle (Free / Program), camera list with
 * select / look-through / rename / delete / colour, preset picker modal, and
 * the selected-camera editor (preset sliders + handheld shake).
 * Rendered from state on update(); list/editor rebuild only when their content
 * signature changes so inputs aren't clobbered mid-edit.
 */
export function createCameraPanel(handlers: CameraPanelHandlers) {
  const panel = byId<HTMLElement>('cameras-panel');
  const viewToggle = byId<HTMLDivElement>('camera-view-toggle');
  const list = byId<HTMLDivElement>('camera-list');
  const addBtn = byId<HTMLButtonElement>('camera-add');
  const editor = byId<HTMLDivElement>('camera-editor');
  const presetParams = byId<HTMLDivElement>('camera-preset-params');
  const shakeToggle = byId<HTMLInputElement>('camera-shake-toggle');
  const shakeControls = byId<HTMLDivElement>('camera-shake-controls');
  const shakeAmount = byId<HTMLInputElement>('camera-shake-amount');
  const shakeAmountValue = byId<HTMLOutputElement>('camera-shake-amount-value');
  const shakeSpeed = byId<HTMLInputElement>('camera-shake-speed');
  const shakeSpeedValue = byId<HTMLOutputElement>('camera-shake-speed-value');
  const aimMode = byId<HTMLSelectElement>('camera-aim-mode');
  const aimBoneBox = byId<HTMLDivElement>('camera-aim-bone');
  const aimActor = byId<HTMLSelectElement>('camera-aim-actor');
  const aimBoneName = byId<HTMLSelectElement>('camera-aim-bonename');
  const aimHeight = byId<HTMLInputElement>('camera-aim-height');
  const aimPointHint = byId<HTMLParagraphElement>('camera-aim-point-hint');
  const setKeyBtn = byId<HTMLButtonElement>('camera-set-key');
  const delKeyBtn = byId<HTMLButtonElement>('camera-del-key');
  const easingRow = byId<HTMLElement>('camera-easing-row');
  const easingSelect = byId<HTMLSelectElement>('camera-key-easing');
  const exportBtn = byId<HTMLButtonElement>('cameras-export');
  const importBtn = byId<HTMLButtonElement>('cameras-import');
  const camerasFileInput = byId<HTMLInputElement>('cameras-file-input');
  const modal = byId<HTMLDivElement>('preset-modal');
  const modalCancel = byId<HTMLButtonElement>('preset-cancel');
  const subjectRow = byId<HTMLElement>('preset-subject-row');
  const subjectSelect = byId<HTMLSelectElement>('preset-subject');
  const presetGrid = byId<HTMLDivElement>('preset-grid');

  let lastListSignature = '';
  let lastEditorSignature = '';
  let renaming = false;

  // --- Preset picker modal ---------------------------------------------------

  addBtn.addEventListener('click', () => openModal());
  modalCancel.addEventListener('click', () => (modal.hidden = true));
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.hidden = true; // click outside the box closes
  });

  function openModal(): void {
    const subjects = handlers.getSubjects();
    subjectRow.hidden = subjects.length === 0;
    subjectSelect.innerHTML = '';
    for (const s of subjects) {
      const opt = document.createElement('option');
      opt.value = s.id;
      opt.textContent = s.label;
      subjectSelect.appendChild(opt);
    }
    if (presetGrid.childElementCount === 0) buildPresetGrid();
    modal.hidden = false;
  }

  function buildPresetGrid(): void {
    for (const preset of PRESETS) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'preset-card';
      card.innerHTML = `<strong>${preset.label}</strong><span>${preset.desc}</span>`;
      card.addEventListener('click', () => {
        modal.hidden = true;
        handlers.onApplyPreset(preset.id, subjectSelect.value || null);
      });
      presetGrid.appendChild(card);
    }
  }

  // --- View toggle -----------------------------------------------------------

  viewToggle.querySelectorAll<HTMLButtonElement>('.seg').forEach((btn) => {
    btn.addEventListener('click', () => handlers.onSetView(btn.dataset.view as 'free' | 'program'));
  });

  // --- Camera list -----------------------------------------------------------

  function rebuildList(): void {
    list.innerHTML = '';
    for (const cam of state.cameras.cameras) {
      const row = document.createElement('div');
      row.className = 'camera-row';
      row.classList.toggle('selected', cam.id === state.selectedCameraId);
      row.addEventListener('click', () => handlers.onSelectCamera(cam.id));

      const swatch = document.createElement('input');
      swatch.type = 'color';
      swatch.className = 'actor-color';
      swatch.value = `#${cam.color.toString(16).padStart(6, '0')}`;
      swatch.title = 'Camera colour';
      swatch.addEventListener('click', (e) => e.stopPropagation());
      swatch.addEventListener('input', () => handlers.onCameraColor(cam.id, swatch.value));

      const name = document.createElement('span');
      name.className = 'actor-name camera-name';
      name.textContent = cam.name;
      name.title = 'Double-click to rename';
      name.addEventListener('dblclick', (e) => {
        e.stopPropagation();
        startRename(name, cam.id, cam.name);
      });

      const eye = document.createElement('button');
      eye.type = 'button';
      eye.className = 'btn icon small camera-eye';
      eye.innerHTML = '&#9678;';
      eye.title = 'Look through this camera';
      eye.classList.toggle('active', state.cameraView === cam.id);
      eye.addEventListener('click', (e) => {
        e.stopPropagation();
        handlers.onLookThrough(cam.id);
      });

      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'btn icon small camera-delete';
      del.innerHTML = '&times;';
      del.title = 'Delete camera';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        handlers.onDeleteCamera(cam.id);
      });

      row.append(swatch, name, eye, del);
      list.appendChild(row);
    }
  }

  function startRename(nameEl: HTMLSpanElement, id: string, current: string): void {
    renaming = true;
    const input = document.createElement('input');
    input.type = 'text';
    input.value = current;
    input.className = 'camera-rename';
    nameEl.replaceWith(input);
    input.focus();
    input.select();
    const commit = (): void => {
      renaming = false;
      const next = input.value.trim();
      handlers.onRenameCamera(id, next.length > 0 ? next : current);
      lastListSignature = ''; // force a rebuild with the fresh name
      update();
    };
    input.addEventListener('blur', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') input.blur();
      if (e.key === 'Escape') {
        input.value = current;
        input.blur();
      }
      e.stopPropagation();
    });
  }

  // --- Selected-camera editor (preset params + shake) --------------------------

  shakeToggle.addEventListener('change', () => {
    if (state.selectedCameraId) {
      handlers.onShakeChange(state.selectedCameraId, { enabled: shakeToggle.checked });
    }
  });
  shakeAmount.addEventListener('input', () => {
    shakeAmountValue.value = shakeAmount.value;
    if (state.selectedCameraId) {
      handlers.onShakeChange(state.selectedCameraId, { amount: Number(shakeAmount.value) });
    }
  });
  shakeSpeed.addEventListener('input', () => {
    shakeSpeedValue.value = shakeSpeed.value;
    if (state.selectedCameraId) {
      handlers.onShakeChange(state.selectedCameraId, { freq: Number(shakeSpeed.value) });
    }
  });

  const emitAim = (): void => {
    if (!state.selectedCameraId) return;
    handlers.onAimChange(state.selectedCameraId, aimMode.value as 'path' | 'bone' | 'point' | 'group', {
      actorId: aimActor.value || undefined,
      bone: aimBoneName.value,
      heightOffset: Number(aimHeight.value) || 0,
    });
  };
  aimMode.addEventListener('change', emitAim);
  aimActor.addEventListener('change', emitAim);
  aimBoneName.addEventListener('change', emitAim);
  aimHeight.addEventListener('change', emitAim);

  setKeyBtn.addEventListener('click', () => handlers.onSetKeyFromView());
  delKeyBtn.addEventListener('click', () => handlers.onDeleteKey());
  easingSelect.addEventListener('change', () => handlers.onKeyEasing(easingSelect.value));

  exportBtn.addEventListener('click', () => handlers.onExportCameras());
  importBtn.addEventListener('click', () => camerasFileInput.click());
  camerasFileInput.addEventListener('change', () => {
    const file = camerasFileInput.files?.[0];
    if (file) handlers.onImportCameras(file);
    camerasFileInput.value = ''; // allow re-importing the same file
  });

  /** Build the preset sliders for the selected camera (label + slider/segments). */
  function rebuildPresetParams(cameraId: string): void {
    presetParams.innerHTML = '';
    const def = findCamera(state.cameras, cameraId);
    const preset = def?.preset ? getPreset(def.preset.id) : null;
    if (!def || !preset || preset.params.length === 0) return;

    const title = document.createElement('p');
    title.className = 'hint camera-preset-title';
    title.textContent = `${preset.label} settings — tweaking re-bakes the path`;
    presetParams.appendChild(title);

    for (const spec of preset.params) {
      const value = def.preset!.params[spec.key] ?? spec.default;
      if (spec.options) {
        const wrap = document.createElement('div');
        wrap.className = 'row column';
        const label = document.createElement('span');
        label.textContent = spec.label;
        const seg = document.createElement('div');
        seg.className = 'row segmented';
        for (const opt of spec.options) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'seg';
          btn.textContent = opt.label;
          btn.classList.toggle('active', opt.value === value);
          btn.addEventListener('click', () => {
            handlers.onPresetParam(cameraId, spec.key, opt.value);
            seg.querySelectorAll('.seg').forEach((b) => b.classList.toggle('active', b === btn));
          });
          seg.appendChild(btn);
        }
        wrap.append(label, seg);
        presetParams.appendChild(wrap);
      } else {
        const label = document.createElement('label');
        label.className = 'row column';
        const caption = document.createElement('span');
        const output = document.createElement('output');
        output.value = String(value);
        caption.append(`${spec.label}: `, output, spec.unit ?? '');
        const slider = document.createElement('input');
        slider.type = 'range';
        slider.min = String(spec.min);
        slider.max = String(spec.max);
        slider.step = String(spec.step);
        slider.value = String(value);
        slider.addEventListener('input', () => {
          output.value = slider.value;
          handlers.onPresetParam(cameraId, spec.key, Number(slider.value));
        });
        label.append(caption, slider);
        presetParams.appendChild(label);
      }
    }
  }

  function updateEditor(): void {
    const def = findCamera(state.cameras, state.selectedCameraId);
    editor.hidden = !def;
    if (!def) {
      lastEditorSignature = '';
      return;
    }

    const signature = JSON.stringify([def.id, def.preset?.id ?? null]);
    if (signature !== lastEditorSignature) {
      lastEditorSignature = signature;
      rebuildPresetParams(def.id);
    }

    // Aim (tracking target) controls.
    if (document.activeElement !== aimMode) aimMode.value = def.aim.mode;
    aimBoneBox.hidden = def.aim.mode !== 'bone';
    aimPointHint.hidden = def.aim.mode !== 'point';
    if (def.aim.mode === 'bone') {
      const subjects = handlers.getSubjects();
      const wanted = subjects.map((s) => s.id).join('|');
      if (aimActor.dataset.subjects !== wanted) {
        aimActor.dataset.subjects = wanted;
        aimActor.innerHTML = '';
        for (const s of subjects) {
          const opt = document.createElement('option');
          opt.value = s.id;
          opt.textContent = s.label;
          aimActor.appendChild(opt);
        }
      }
      if (document.activeElement !== aimActor) aimActor.value = def.aim.actorId;
      if (document.activeElement !== aimBoneName) aimBoneName.value = def.aim.bone;
      if (document.activeElement !== aimHeight) aimHeight.value = String(def.aim.heightOffset);
    }

    // Keyframe row: setting a key needs the Free view; deleting needs a
    // selection and at least two keys (a camera always keeps one).
    setKeyBtn.disabled = state.cameraView !== 'free';
    setKeyBtn.title =
      state.cameraView !== 'free'
        ? 'Switch to Free view to frame a keyframe'
        : 'Add/update a keyframe at the playhead from the Free view (K)';
    const keySelected = state.selectedKeyframe !== null && def.keyframes[state.selectedKeyframe];
    delKeyBtn.disabled = !keySelected || def.keyframes.length <= 1;
    easingRow.hidden = !keySelected;
    if (keySelected && document.activeElement !== easingSelect) {
      easingSelect.value = def.keyframes[state.selectedKeyframe!].easing;
    }

    shakeToggle.checked = def.shake?.enabled ?? false;
    shakeControls.hidden = !shakeToggle.checked;
    if (def.shake) {
      if (document.activeElement !== shakeAmount) {
        const amount = handlers.shakeAmountOf(def.shake.posAmp);
        shakeAmount.value = String(amount);
        shakeAmountValue.value = String(amount);
      }
      if (document.activeElement !== shakeSpeed) {
        shakeSpeed.value = String(def.shake.freq);
        shakeSpeedValue.value = String(def.shake.freq);
      }
    }
  }

  /** Reflect current state into the panel (called on every notify()). */
  function update(): void {
    panel.hidden = !hasAnimation();
    if (panel.hidden) return;

    const hasCameras = state.cameras.cameras.length > 0;
    exportBtn.disabled = !hasCameras;
    viewToggle.querySelectorAll<HTMLButtonElement>('.seg').forEach((btn) => {
      const view = btn.dataset.view;
      btn.classList.toggle(
        'active',
        view === 'program' ? state.cameraView === 'program' : state.cameraView === 'free'
      );
      if (view === 'program') btn.disabled = !hasCameras;
    });

    if (!renaming) {
      const signature = JSON.stringify([
        state.selectedCameraId,
        state.cameraView,
        state.cameras.cameras.map((c) => [c.id, c.name, c.color]),
      ]);
      if (signature !== lastListSignature) {
        lastListSignature = signature;
        rebuildList();
      }
    }

    updateEditor();
  }

  return { update };
}

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}
